import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useOrg } from '@/contexts/OrgContext';
import { BUTTON_RADIUS, LAYOUT_RADIUS, fontStack, getPreset, hexToHsl, parseForcedTheme } from '@/lib/theme-presets';
import { ThemeContext, type ThemeSettings } from '@/contexts/ThemeContext';

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  // The admin theme-preview iframe forces a preset (+ the admin's unsaved
  // tweaks) from its query string. Forced themes are applied to this document
  // only: nothing is fetched, subscribed to, or ever written.
  const forcedTheme = useMemo(() => parseForcedTheme(window.location.pathname, window.location.search), []);
  const [themeSettings, setThemeSettings] = useState<ThemeSettings | null>(forcedTheme);

  // Wait until we know whose site this is: fetching during resolution would
  // paint the fallback org's theme first (and, in admin preview frames, ours).
  const { orgId, loading: orgLoading } = useOrg();

  const applyTheme = useCallback((settings: ThemeSettings) => {
    const root = document.documentElement;
    const preset = getPreset(settings.theme_name);

    // Colours — derive HSL from the shared preset hex (single source of truth),
    // with an optional per-tenant primary override.
    const primaryHsl = settings.primary_color
      ? hexToHsl(settings.primary_color)
      : preset.hsl?.primary ?? hexToHsl(preset.primaryColor);
    root.style.setProperty('--primary', primaryHsl);
    root.style.setProperty('--ring', primaryHsl);

    // Button text colour (custom or default white).
    const buttonTextHsl = settings.button_text_color
      ? hexToHsl(settings.button_text_color)
      : '0 0% 100%';
    root.style.setProperty('--primary-foreground', buttonTextHsl);
    root.style.setProperty('--secondary', preset.hsl?.secondary ?? hexToHsl(preset.secondaryColor));
    root.style.setProperty('--accent', preset.hsl?.accent ?? hexToHsl(preset.accentColor));
    // NOTE: deliberately do NOT override --background / --foreground here. Those
    // are toggled per colour-scheme by next-themes via the .dark class; pinning
    // them inline would break dark mode for every themed tenant. Presets with
    // their own light surface publish it as --theme-light-* and flag the root;
    // tenant-premium.css maps those onto --background/--foreground in light mode only.
    if (preset.hsl?.background && preset.hsl?.foreground) {
      root.style.setProperty('--theme-light-background', preset.hsl.background);
      root.style.setProperty('--theme-light-foreground', preset.hsl.foreground);
      root.setAttribute('data-theme-surface', preset.id);
    } else {
      root.style.removeProperty('--theme-light-background');
      root.style.removeProperty('--theme-light-foreground');
      root.removeAttribute('data-theme-surface');
    }

    // Corner geometry. `--radius` is for cards, inputs, and layout chrome;
    // `--button-radius` is only for CTA/button shape. Keep them separate so
    // pill button themes do not make every card look like an oval.
    const layoutStyle =
      settings.theme_name === 'warm-welcome' && settings.layout_style === 'playful-rounded'
        ? 'modern-minimal'
        : settings.layout_style || preset.layoutStyle;
    const buttonStyle =
      settings.theme_name === 'warm-welcome' && settings.button_style === 'pill'
        ? 'rounded'
        : settings.button_style;
    const buttonRadius = BUTTON_RADIUS[buttonStyle] || BUTTON_RADIUS.rounded;
    root.style.setProperty('--radius', LAYOUT_RADIUS[layoutStyle] || LAYOUT_RADIUS.classic);
    root.style.setProperty('--button-radius', buttonRadius);

    // Fonts — full family stacks with real fallbacks (the bare family names used
    // previously were never loaded, and "Palo" overwrote the brand display font).
    root.style.setProperty('--font-display', fontStack(settings.heading_font || preset.headingFont));
    root.style.setProperty('--font-body', fontStack(settings.body_font || preset.bodyFont));
    document.body.style.fontFamily = fontStack(settings.body_font || preset.bodyFont);

    // Layout personality marker — read by `[data-layout]` CSS rules so the real
    // public pages (not just page-builder sections) change with the template.
    root.setAttribute('data-layout', layoutStyle);
    // Which composition (navbar / hero / services / footer…) the site wears.
    root.setAttribute('data-variant', preset.id);
  }, []);

  const fetchThemeSettings = useCallback(
    async (currentOrgId: string) => {
      try {
        // Query directly — theme_settings is publicly readable and the edge function
        // requires auth which anonymous site visitors don't have.
        const { data, error } = await supabase
          .from('theme_settings')
          .select('*')
          .eq('organization_id', currentOrgId)
          .maybeSingle();

        if (error) throw error;

        if (data) {
          setThemeSettings(data as ThemeSettings);
          applyTheme(data as ThemeSettings);
        }
      } catch (error) {
        console.error('Error fetching theme settings:', error);
      }
    },
    [applyTheme]
  );

  useEffect(() => {
    if (forcedTheme) {
      applyTheme(forcedTheme);
      return;
    }
    if (!orgId || orgLoading) return;
    fetchThemeSettings(orgId);

    const handleThemeUpdate = (event: Event) => {
      const settings = (event as CustomEvent<ThemeSettings>).detail;
      if (settings) {
        setThemeSettings(settings);
        applyTheme(settings);
      }
    };

    window.addEventListener('theme-settings-updated', handleThemeUpdate);

    // Subscribe to realtime changes for THIS org only
    const channel = supabase
      .channel(`theme-changes-${orgId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'theme_settings',
          filter: `organization_id=eq.${orgId}`,
        },
        (payload) => {
          if (payload.new) {
            const newSettings = payload.new as ThemeSettings;
            setThemeSettings(newSettings);
            applyTheme(newSettings);
          }
        }
      )
      .subscribe();

    return () => {
      window.removeEventListener('theme-settings-updated', handleThemeUpdate);
      supabase.removeChannel(channel);
    };
  }, [forcedTheme, orgId, orgLoading, fetchThemeSettings, applyTheme]);

  return (
    <ThemeContext.Provider value={themeSettings}>
      {children}
    </ThemeContext.Provider>
  );
};
